import React, { useEffect, useMemo, useState } from "react";
import { Canvas } from "react-three-fiber";
import Slider from "react-rangeslider";

import { ReverbScene } from "./ReverbScene";
import * as audio from "./audio";

import "./ReverbApp.scss";
import { Intro } from "./Intro";

const BASE_WIDTH = 3;
const BASE_HEIGHT = 2;
const BASE_DEPTH = 3;

const HEIGHTS = {
  adult: 1,
  teenager: 0.75,
  baby: 0.25,
};

const MIN_ROOM_SIZE = HEIGHTS.adult / BASE_HEIGHT;

export const ReverbApp: React.FC = () => {
  let [intro, setIntro] = useState(true);
  let [roomMaterial, setRoomMaterial] = useState(audio.ROOM_MATERIALS[0].value);
  let [roomScale, setRoomScale] = useState(1.5);
  let [listenerRelativePosition, setListenerRelativePosition] = useState<
    [x: number, y: number]
  >([0, 0]);
  let [listenerHeight, setListenerHeight] = useState<
    "baby" | "teenager" | "adult"
  >("adult");

  let roomSize = useMemo(
    () =>
      [
        BASE_WIDTH * roomScale,
        BASE_HEIGHT * roomScale,
        BASE_DEPTH * roomScale,
      ] as [number, number, number],
    [roomScale]
  );

  let listenerAbsolutePosition = useMemo(
    () =>
      [
        (listenerRelativePosition[0] * roomSize[0]) / 2,
        -roomSize[1] / 2 + HEIGHTS[listenerHeight],
        (listenerRelativePosition[1] * roomSize[1]) / 2,
      ] as [number, number, number],
    [listenerRelativePosition, listenerHeight, roomSize]
  );

  useEffect(() => {
    audio.setRoom(roomSize, roomMaterial);
  }, [roomSize, roomMaterial]);
  useEffect(() => {
    audio.setListenerPosition(listenerAbsolutePosition);
  }, [listenerAbsolutePosition]);

  let start = () => {
    audio.start();
    setIntro(false);
  };

  return (
    <div className="reverbApp">
      {!intro && (
        <Canvas
          colorManagement={false}
        >
          <ReverbScene
            size={roomSize}
            listenerPosition={listenerAbsolutePosition}
          />
        </Canvas>
      )}
      {!intro && (
        <div className="reverbAppControls">
          <div className="inputGroup">
            <label>Room size</label>
            <Slider
              min={MIN_ROOM_SIZE}
              max={10}
              step={0.1}
              value={roomScale}
              tooltip={false}
              onChange={setRoomScale}
            />
          </div>
          <div className="inputGroup">
            <label>Room material</label>
            <select
              value={roomMaterial}
              onChange={(evt) => setRoomMaterial(evt.target.value)}
            >
              {audio.ROOM_MATERIALS.map((m) => (
                <option value={m.value} key={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>
          <div className="inputGroup">
            <label>Listener height</label>
            <select
              value={listenerHeight}
              onChange={(evt) =>
                setListenerHeight(
                  evt.target.value as "adult" | "teenager" | "baby"
                )
              }
            >
              <option value="adult">Adult</option>
              <option value="teenager">Teenager</option>
              <option value="baby">Baby</option>
            </select>
          </div>
          <div className="inputGroup">
            <label>Listener position</label>
            <div className="inputRow">
              <Slider
                min={-1}
                max={1}
                step={0.01}
                value={listenerRelativePosition[0]}
                tooltip={false}
                onChange={(x) =>
                  setListenerRelativePosition((p) => [x, p[1]])
                }
              />
              <Slider
                min={-1}
                max={1}
                step={0.01}
                value={listenerRelativePosition[1]}
                tooltip={false}
                onChange={(y) =>
                  setListenerRelativePosition((p) => [p[0], y])
                }
              />
            </div>
          </div>
        </div>
      )}
      {intro && <Intro onDismiss={start} />}
    </div>
  );
};
